export type AuthDiagnosticSource = 'supabase_middleware_auth' | 'auth_guard' | 'session_security';
export type SessionStartedAtCookieScope = 'host' | 'parent' | 'legacy_unmarked';

export type AuthDiagnosticEvent =
  | 'fail_open'
  | 'redirect_login'
  | 'session_expired'
  | 'account_deactivated'
  | 'client_portal_redirect'
  | 'identity_revalidation_unavailable'
  | 'identity_revalidation_blocked'
  | 'initial_user_missing'
  | 'visibility_recovery_start'
  | 'visibility_recovery_success'
  | 'visibility_recovery_failed'
  | 'auth_state_signed_out'
  | 'login_bounce_recorded'
  | 'login_bounce_loop_broken'
  | 'login_cookie_missing'
  | 'login_client_session_verified'
  | 'login_workspace_opened'
  | 'login_workspace_recovery_reload'
  | 'idle_timeout';

export type AuthDiagnosticPayload = {
  source: AuthDiagnosticSource;
  event: AuthDiagnosticEvent;
  reason?: string;
  pathname?: string;
  authErrorKind?: string;
  /** HTTP status from Auth REST (429, 503, …). PHI-free. */
  authErrorStatus?: number;
  /** GoTrue error code (`bad_jwt`, `over_request_rate_limit`, …). PHI-free. */
  authErrorCode?: string;
  sessionAgeMinutes?: number;
  sessionAgeHours?: number;
  sessionAgeCapped?: boolean;
  sessionStartedAtCookieScope?: SessionStartedAtCookieScope;
  visibilityState?: string;
  online?: boolean;
  hasFocus?: boolean;
};

const AUTH_ERROR_KINDS = new Set([
  'soft_null',
  'other',
  'Error',
  'AuthError',
  'AuthApiError',
  'AuthUnknownError',
  'CustomAuthError',
  'AuthSessionMissingError',
  'AuthInvalidTokenResponseError',
  'AuthInvalidCredentialsError',
  'AuthImplicitGrantRedirectError',
  'AuthPKCEGrantCodeExchangeError',
  'AuthPKCECodeVerifierMissingError',
  'AuthRetryableFetchError',
  'AuthRefreshDiscardedError',
  'AuthWeakPasswordError',
  'AuthInvalidJwtError',
  'string',
  'number',
  'boolean',
  'object',
  'function',
  'symbol',
  'bigint',
]);

// Keep in lockstep with the deployed @supabase/auth-js ErrorCode union.
// Unknown/future codes collapse to a constant rather than entering logs.
const GOTRUE_ERROR_CODES = new Set([
  'unexpected_failure',
  'validation_failed',
  'bad_json',
  'email_exists',
  'phone_exists',
  'bad_jwt',
  'not_admin',
  'no_authorization',
  'user_not_found',
  'session_not_found',
  'session_expired',
  'refresh_token_not_found',
  'refresh_token_already_used',
  'flow_state_not_found',
  'flow_state_expired',
  'signup_disabled',
  'user_banned',
  'provider_email_needs_verification',
  'invite_not_found',
  'bad_oauth_state',
  'bad_oauth_callback',
  'oauth_provider_not_supported',
  'unexpected_audience',
  'single_identity_not_deletable',
  'email_conflict_identity_not_deletable',
  'identity_already_exists',
  'email_provider_disabled',
  'phone_provider_disabled',
  'too_many_enrolled_mfa_factors',
  'mfa_factor_name_conflict',
  'mfa_factor_not_found',
  'mfa_ip_address_mismatch',
  'mfa_challenge_expired',
  'mfa_verification_failed',
  'mfa_verification_rejected',
  'insufficient_aal',
  'captcha_failed',
  'saml_provider_disabled',
  'manual_linking_disabled',
  'sms_send_failed',
  'email_not_confirmed',
  'phone_not_confirmed',
  'reauth_nonce_missing',
  'saml_relay_state_not_found',
  'saml_relay_state_expired',
  'saml_idp_not_found',
  'saml_assertion_no_user_id',
  'saml_assertion_no_email',
  'user_already_exists',
  'sso_provider_not_found',
  'saml_metadata_fetch_failed',
  'saml_idp_already_exists',
  'sso_domain_already_exists',
  'saml_entity_id_mismatch',
  'conflict',
  'provider_disabled',
  'user_sso_managed',
  'reauthentication_needed',
  'same_password',
  'reauthentication_not_valid',
  'otp_expired',
  'otp_disabled',
  'identity_not_found',
  'weak_password',
  'over_request_rate_limit',
  'over_email_send_rate_limit',
  'over_sms_send_rate_limit',
  'bad_code_verifier',
  'anonymous_provider_disabled',
  'hook_timeout',
  'hook_timeout_after_retry',
  'hook_payload_over_size_limit',
  'hook_payload_invalid_content_type',
  'request_timeout',
  'mfa_phone_enroll_not_enabled',
  'mfa_phone_verify_not_enabled',
  'mfa_totp_enroll_not_enabled',
  'mfa_totp_verify_not_enabled',
  'mfa_webauthn_enroll_not_enabled',
  'mfa_webauthn_verify_not_enabled',
  'mfa_verified_factor_exists',
  'invalid_credentials',
  'email_address_not_authorized',
  'email_address_invalid',
]);

const SESSION_AGE_LOG_MAX_MINUTES = 7 * 24 * 60;
const SESSION_AGE_LOG_MAX_HOURS = 7 * 24;

const PARAM_AFTER_SEGMENTS = new Set([
  'assessments',
  'care-plans',
  'clients',
  'complaints',
  'consents',
  'documents',
  'evidence',
  'goals',
  'incidents',
  'journeys',
  'medications',
  'policies',
  'reports',
  'staff',
  'tasks',
  'users',
  'visits',
  'waitlist',
  'workflows',
]);

function pathnameOnly(value: string | undefined) {
  if (!value) return '/';
  try {
    return new URL(value, 'https://app.statura.care').pathname || '/';
  } catch {
    return value.split('?')[0] || '/';
  }
}

function isLikelyDynamicSegment(segment: string, previousSegment?: string) {
  if (!segment) return false;
  if (previousSegment && PARAM_AFTER_SEGMENTS.has(previousSegment)) return true;
  if (/^\d+$/.test(segment)) return true;
  if (/^[0-9a-f]{8,}(?:-[0-9a-f]{4,}){2,}$/i.test(segment)) return true;
  if (/^[0-9a-f]{12,}$/i.test(segment) && /\d/.test(segment)) return true;
  if (segment.length > 32) return true;
  if (/%40|@/.test(segment)) return true;
  return false;
}

export function sanitizeAuthDiagnosticPath(value: string | undefined) {
  const pathname = pathnameOnly(value);
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return '/';

  const sanitized = segments.map((segment, index) => {
    const previous = index > 0 ? segments[index - 1] : undefined;
    return isLikelyDynamicSegment(segment, previous) ? ':param' : segment;
  });

  return `/${sanitized.join('/')}`;
}

export function authErrorKind(error: unknown) {
  if (!error) return undefined;
  if (error instanceof Error) {
    if (error.message.includes('getAuthUserWithRetry: getUser() returned a null user')) {
      return 'soft_null';
    }
    const kind = error.name || 'Error';
    return AUTH_ERROR_KINDS.has(kind) ? kind : 'other';
  }
  const kind = typeof error;
  return AUTH_ERROR_KINDS.has(kind) ? kind : 'other';
}

/**
 * HTTP status behind an Auth REST failure, when there is one.
 *
 * Supabase `AuthError` carries `status`/`code`; plain network throws do not.
 */
export function authErrorStatus(error: unknown): number | undefined {
  const status = (error as { status?: unknown } | null | undefined)?.status;
  return typeof status === 'number' && Number.isInteger(status) && status >= 0 && status <= 599
    ? status
    : undefined;
}

/** GoTrue error code behind an Auth REST failure, when there is one. */
export function authErrorCode(error: unknown): string | undefined {
  const code = (error as { code?: unknown } | null | undefined)?.code;
  if (typeof code !== 'string' || code.length === 0) return undefined;
  return GOTRUE_ERROR_CODES.has(code) ? code : 'other';
}

function sanitizedAuthErrorKind(value: unknown) {
  return typeof value === 'string' && AUTH_ERROR_KINDS.has(value) ? value : undefined;
}

function sanitizedAuthErrorCode(value: unknown) {
  if (typeof value !== 'string' || value.length === 0) return undefined;
  return GOTRUE_ERROR_CODES.has(value) ? value : 'other';
}

function boundedWholeNumber(value: unknown, max: number) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return undefined;
  return Math.min(Math.max(Math.floor(value), 0), max);
}

function sanitizedSessionStartedAtCookieScope(
  value: unknown,
): SessionStartedAtCookieScope | undefined {
  return value === 'host' || value === 'parent' || value === 'legacy_unmarked' ? value : undefined;
}

export function logAuthDiagnostic(payload: AuthDiagnosticPayload) {
  console.info(
    JSON.stringify({
      level: 'info',
      source: payload.source,
      event: payload.event,
      reason: payload.reason,
      pathname: sanitizeAuthDiagnosticPath(payload.pathname),
      authErrorKind: sanitizedAuthErrorKind(payload.authErrorKind),
      authErrorStatus: authErrorStatus({ status: payload.authErrorStatus }),
      authErrorCode: sanitizedAuthErrorCode(payload.authErrorCode),
      sessionAgeMinutes: boundedWholeNumber(payload.sessionAgeMinutes, SESSION_AGE_LOG_MAX_MINUTES),
      sessionAgeHours: boundedWholeNumber(payload.sessionAgeHours, SESSION_AGE_LOG_MAX_HOURS),
      sessionAgeCapped:
        typeof payload.sessionAgeCapped === 'boolean' ? payload.sessionAgeCapped : undefined,
      sessionStartedAtCookieScope: sanitizedSessionStartedAtCookieScope(
        payload.sessionStartedAtCookieScope,
      ),
      visibilityState: payload.visibilityState,
      online: payload.online,
      hasFocus: payload.hasFocus,
    }),
  );
}

export function reportClientAuthDiagnostic(
  event: AuthDiagnosticEvent,
  opts: {
    source?: Extract<AuthDiagnosticSource, 'auth_guard' | 'session_security'>;
    reason?: string;
    authErrorKind?: string;
  } = {},
) {
  if (typeof window === 'undefined') return;

  const payload: AuthDiagnosticPayload = {
    source: opts.source ?? 'auth_guard',
    event,
    reason: opts.reason,
    authErrorKind: opts.authErrorKind,
    pathname: sanitizeAuthDiagnosticPath(window.location.pathname),
    visibilityState: document.visibilityState,
    online: navigator.onLine,
    hasFocus: document.hasFocus(),
  };

  const body = JSON.stringify(payload);
  try {
    if (navigator.sendBeacon) {
      const sent = navigator.sendBeacon(
        '/api/auth/diagnostics',
        new Blob([body], { type: 'application/json' }),
      );
      if (sent) return;
    }
    void fetch('/api/auth/diagnostics', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {
      logAuthDiagnostic(payload);
    });
  } catch {
    logAuthDiagnostic(payload);
  }
}
